import React, { useState, useCallback } from 'react';
import Card, { CardRow } from 'components/styleguide/card/Card';
import Sheet from 'components/styleguide/sheet_modal/Sheet';
import ImageUpload from '../../styleguide/image_upload/ImageUpload';
import { getCSRFToken } from 'lib/request';

interface CompanyLogoCardProps {
  logoUrl: string;
  url: string;
  errors?: Record<string, string>;
}

const CompanyLogoCard: React.FC<CompanyLogoCardProps> = ({
  logoUrl: initialLogoUrl,
  url,
  errors,
}) => {
  const [logoUrl, setLogoUrl] = useState(initialLogoUrl || '');
  const [isOpen, setIsOpen] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFileChange = (selectedFile: File) => {
    setFile(selectedFile);
    setPreview(URL.createObjectURL(selectedFile));
    setError(null);
  };

  const handleSave = useCallback(async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      const formData = new FormData();
      formData.append('company[logo]', file || '');

      const response = await fetch(url, {
        method: 'PATCH',
        headers: {
          'X-CSRF-Token': getCSRFToken() || '',
          'Accept': 'application/json',
        },
        body: formData,
      });

      const data = await response.json();
      if (response.ok && data.success) {
        setLogoUrl(data.logo_url || preview || logoUrl);
        setIsOpen(false);
        setPreview(null);
        setFile(null);
        setError(null);
      } else {
        setError(data.errors?.logo || null);
      }
    } catch (error) {
      // Handle error (e.g., show notification)
    } finally {
      setIsSubmitting(false);
    }
  }, [isSubmitting, file, url, preview, logoUrl]);

  const handleClose = () => {
    setIsOpen(false);
    setPreview(null);
    setFile(null);
    setError(null);
  };

  return (
    <>
      <Card
        title="Company logo"
        iconName="image"
        context="company"
        tag={logoUrl
          ? undefined
          : errors?.logo
            ? { variant: 'error' as const, icon: 'triangleAlert' as const, children: 'Mandatory' }
            : { variant: 'warning' as const, icon: 'circleAlert' as const, children: 'Missing' }}
        onEdit={() => setIsOpen(true)}
      >
        <CardRow context="company">
          <ImageUpload imageUrl={logoUrl} readOnly />
        </CardRow>
      </Card>

      {isOpen && (
        <Sheet
          title="Edit company logo"
          onClose={handleClose}
          context="company"
          showCancelButton={false}
          continueButtonText="Save"
          onContinue={handleSave}
        >
          <ImageUpload
            imageUrl={preview || logoUrl}
            onChange={handleFileChange}
            error={error || undefined}
          />
        </Sheet>
      )}
    </>
  );
};

export default CompanyLogoCard;
